import { LS_ITEMS, addItemToLS, getItemFromLS } from 'utils/localStorage'
import { atom, selector } from 'recoil'

import { advFilterDefs } from 'components/Analytics/AnalyticsPage/AnalyticsDefs'
import cloneDeep from 'lodash/cloneDeep'

export const bhaImagesAtom = atom({
  key: 'bhaImageAtomKey',
  default: [],
})

export const themeAtom = atom({
  key: 'themeKey',
  default: getItemFromLS(LS_ITEMS.theme, LS_ITEMS.theme) || 'dark',
})

export const themeSelector = selector({
  key: 'themeSelectorKey',
  get: ({ get }) => {
    return get(themeAtom)
  },
  set: ({ set }, newValue) => {
    if (newValue && newValue.length > 0) {
      addItemToLS(LS_ITEMS.theme, LS_ITEMS.theme, newValue)
    }
    set(themeAtom, newValue)
  },
})

export const refreshSurveyProgramGridAtom = atom({
  key: 'refreshSurveyProgramGridKey',
  default: false,
})

export const onlyShowFollowingWellsAtom = atom({
  key: 'onlyShowFolowingWellsKey',
  default: false,
})

export const showAllWellsAtom = atom({
  key: 'showAllWellsKey',
  default: false,
})

export const dateBaseTreeUpdateNameAtom = atom({
  key: 'dateBaseTreeUpdateNameAtomKey',
  default: null,
})

export const dateBaseTreeSelectedPageAtom = atom({
  key: 'dateBaseTreeSelectedPageAtomKey',
  default: null,
})

export const currentWellAtom = atom({
  key: 'currentWellAtomKey',
  default: getItemFromLS(LS_ITEMS.currentWell, LS_ITEMS.currentWell) || {
    wellName: '',
    organization: '',
  },
})

export const currentWellSelector = selector({
  key: 'currentWellSelectorKey',
  get: ({ get }) => {
    const currWell = get(currentWellAtom)
    if (currWell.wellName !== '' && currWell.organization !== '') {
      addItemToLS(LS_ITEMS.databaseOrg, LS_ITEMS.databaseOrg, currWell?.organization)
    }
    return currWell
  },
  set: ({ set }, newValue) => {
    if (newValue && newValue.hasOwnProperty('organization') && newValue.hasOwnProperty('wellName')) {
      addItemToLS(LS_ITEMS.currentWell, LS_ITEMS.currentWell, newValue)
      if (newValue.wellName !== '' && newValue.organization !== '') {
        addItemToLS(LS_ITEMS.databaseOrg, LS_ITEMS.databaseOrg, newValue?.organization)
      }
    }
    set(currentWellAtom, newValue)
  },
})

export const defaultWellDetails = {
  actualWell: '',
  wellStatus: '',
  operator: '',
  rig: '',
  jobNum: '',
  lat: 29.7604,
  lng: -95.3698,
}

export const currentWellDetailsAtom = atom({
  key: 'currentWellDetailsAtomKey',
  default: getItemFromLS(LS_ITEMS.currentWellDetails, LS_ITEMS.currentWellDetails) || defaultWellDetails,
})

export const currentWellDetailsSelector = selector({
  key: 'currentWellDetailsSelectorKey',
  get: ({ get }) => {
    const currWellDetails = get(currentWellDetailsAtom)
    return currWellDetails
  },
  set: ({ set }, newValue) => {
    if (newValue) {
      addItemToLS(LS_ITEMS.currentWellDetails, LS_ITEMS.currentWellDetails, newValue)
    }
    set(currentWellDetailsAtom, newValue)
  },
})

export const currentBhaAtom = atom({
  key: 'currentBhaKey',
  default: {
    defaultBhaAtom: null,
    bhaNumForPdf: null,
    bhaDescription: null,
    pdfDocData: null,
    pdfFileName: null,
  },
})

export const currentAwsRegionAtom = atom({
  key: 'currentAwsRegionKey',
  default: 'us-east-2',
})

export const userPrefsAtom = atom({
  key: 'userPrefsAtomKey',
  default: '',
})

export const userUserRoleAtom = atom({
  key: 'userRoleAtomKey',
  default: getItemFromLS('auth', 'currentAuth')?.role || {},
})

export const userUserDefaultRoleAtom = atom({
  key: 'userDefaultRoleAtomKey',
  default: getItemFromLS('auth', 'defaultAuth')?.role || {},
})

export const userRoleSelector = selector({
  key: 'userRoleSelectorKey',
  get: ({ get }) => {
    const currRole = get(userUserRoleAtom)
    return currRole
  },
  set: ({ set }, newValue) => {
    set(userUserRoleAtom, newValue)
  },
})

export const userDefaultRoleSelector = selector({
  key: 'userDefaultRoleSelectorKey',
  get: ({ get }) => {
    const currRole = get(userUserDefaultRoleAtom)
    return currRole
  },
  set: ({ set }, newValue) => {
    set(userUserDefaultRoleAtom, newValue)
  },
})

export const wellViewsOpenAtom = atom({
  key: 'wellViewsOpenAtomKey',
  default: false,
})

export const analyticsSelectedWells = atom({
  key: 'analyticsSelectedWellsKey',
  default: [],
})

export const selectedWellRigMap = atom({
  key: 'selectedWellRigMapKey',
  default: {},
})

export const shippingTicketLayoutAtom = atom({
  key: 'shippingTicketLayoutKey',
  default: [
    { size: '40%', visible: true },
    { size: '50%', visible: true },
  ],
})

export const bidLayoutAtom = atom({
  key: 'bidLayoutKey',
  default: [
    { size: '40%', visible: true },
    { size: '50%', visible: true },
  ],
})

export const bidAddressesLayoutAtom = atom({
  key: 'bidAddressesLayoutKey',
  default: [
    { size: '50%', visible: true },
    { size: '50%', visible: true },
    { size: '50%', visible: true },
  ],
})

export const shippingTicketAddressesLayoutAtom = atom({
  key: 'shippingTicketAddressesLayoutKey',
  default: [
    { size: '50%', visible: true },
    { size: '50%', visible: true },
    { size: '50%', visible: true },
  ],
})

export const operatorPropertiesLayoutAtom = atom({
  key: 'operatorPropertiesLayoutKey',
  default: [
    { size: '60%', visible: true },
    { size: '30%', visible: true },
    { size: '70%', visible: true },
    { size: '50%', visible: true },
  ],
})

export const targetsLayoutAtom = atom({
  key: 'targetsLayoutAtomKey',
  default: [
    { size: '30%', visible: true },
    { size: '50%', visible: true },
  ],
})

export const templatesLayoutAtom = atom({
  key: 'templatesLayoutAtomKey',
  default: [
    { size: '30%', visible: true },
    { size: '50%', visible: true },
  ],
})

export const selectedTargetAtom = atom({
  key: 'selectedTargetAtomKey',
  default: null,
})

export const selectedTemplateAtom = atom({
  key: 'selectedTemplateAtomKey',
  default: null,
})

export const fieldPropertiesLayoutAtom = atom({
  key: 'fieldPropertiesLayoutKey',
  default: [
    { size: '60%', visible: true },
    { size: '50%', visible: true },
  ],
})

export const facilityPropertiesLayoutAtom = atom({
  key: 'facilityPropertiesLayoutKey',
  default: [
    { size: '50%', visible: true },
    { size: '50%', visible: true },
  ],
})

export const wellPropertiesLayoutAtom = atom({
  key: 'wellPropertiesLayoutKey',
  default: [
    { size: '65%', visible: true },
    { size: '65%', visible: true },
    { size: '50%', visible: true },
  ],
})

export const plannedWellPropertiesLayoutAtom = atom({
  key: 'plannedWellPropertiesLayoutAtomKey',
  default: [
    { size: '20%', visible: true },
    { size: '50%', visible: true },
    { size: '35%', visible: true },
    { size: '20%', visible: true },
  ],
})

export const actualWellPropertiesLayoutAtom = atom({
  key: 'actualWellPropertiesLayoutAtomKey',
  default: [
    { size: '20%', visible: true },
    { size: '50%', visible: true },
    { size: '35%', visible: true },
    { size: '35%', visible: true },
  ],
})

export const surveyPropertiesLayoutAtom = atom({
  key: 'surveyPropertiesLayoutKey',
  default: [{ size: '50%', visible: true }],
})

export const pipeTallyLayoutAtom = atom({
  key: 'pipeTallLayoutKey',
  default: [{ size: '50%', visible: true }],
})

export const personnelUtilizationLayoutAtom = atom({
  key: 'personnelUtilizationLayoutKey',
  default: [{ size: '30%', visible: true }],
})

export const databaseTreeLayoutAtom = atom({
  key: 'databaseTreeLayouKey',
  default: [{ size: '30%', visible: true }],
})

export const soloCloudLayoutAtom = atom({
  key: 'soloCloudLayoutKey',
  default: [
    { size: '50%', visible: true },
    { size: '50%', visible: true },
  ],
})

export const assetSearchLayoutAtom = atom({
  key: 'assetSearchAtomKey',
  default: [
    { size: '40%', visible: true },
    { size: '50%', visible: true },
  ],
})

export const selectedAssetSearchAtom = atom({
  key: 'selectedAssetSearchAtomKey',
  default: null,
})

export const analyticsFilterDefsAtom = atom({
  key: 'analyticsFilterDefsAtomKey',
  default: cloneDeep(advFilterDefs),
})

export const chartInfoTipDisplayAtom = atom({
  key: 'chartInfoTipDisplayAtomKey',
  default: true,
})

export const selectedWallplotElementAtom = atom({
  key: 'selectedWallplotElementAtom',
  default: null,
})

export const selectedWallplotElementSelector = selector({
  key: 'selectedWallplotElementSelector',
  get: ({ get }) => {
    const currVal = get(selectedWallplotElementAtom)
    return currVal
  },
  set: ({ set }, newValue) => {
    set(selectedWallplotElementAtom, newValue)
  },
})

export const databaseOrgAtom = atom({
  key: 'databaseOrgAtomKey',
  default: getItemFromLS(LS_ITEMS.databaseOrg, LS_ITEMS.databaseOrg) || '',
})

export const databaseDefaultOrgAtom = atom({
  key: 'databaseDefaultOrgAtomKey',
  default: getItemFromLS(LS_ITEMS.databaseDefaultOrg, LS_ITEMS.databaseDefaultOrg) || '',
})

export const databaseOrgSelector = selector({
  key: 'databaseOrgSelectorKey',
  get: ({ get }) => {
    const currOrg = get(databaseOrgAtom)
    return currOrg
  },
  set: ({ set }, newValue) => {
    if (newValue && newValue.length > 0) {
      addItemToLS(LS_ITEMS.databaseOrg, LS_ITEMS.databaseOrg, newValue)
    }
    set(databaseOrgAtom, newValue)
  },
})

export const databaseDefaultOrgSelector = selector({
  key: 'databaseDefaultOrgSelectorKey',
  get: ({ get }) => {
    const currOrg = get(databaseDefaultOrgAtom)
    return currOrg
  },
  set: ({ set }, newValue) => {
    if (newValue && newValue.length > 0) {
      addItemToLS(LS_ITEMS.databaseDefaultOrg, LS_ITEMS.databaseDefaultOrg, newValue)
    }
    set(databaseDefaultOrgAtom, newValue)
  },
})
